import { apiFetch } from '@/platform/lib/api';
import { CURRENT_TERMS_VERSION } from '@/platform/legal/termsVersion';

/** Optional fields visible on GET /users/{id} when toggled on. Handle is always public. */
export type ProfilePublicSettings = {
  displayName?: boolean;
  bio?: boolean;
  email?: boolean;
  youtubeChannelUrl?: boolean;
  /** When false, hides haul ROI/sales on public profile. Default visible when unset. */
  showHaulStats?: boolean;
};

/** Full profile for signed-in user (sync / settings). */
export type UserProfile = {
  id: string;
  displayName?: string;
  handle?: string;
  handleChosen?: boolean;
  bio?: string;
  email?: string;
  avatarUrl?: string;
  vendor?: boolean;
  youtubeChannelUrl?: string;
  profilePublic?: ProfilePublicSettings;
  termsAcceptedVersion?: string;
};

/** Public profile returned by GET /users/{id}. */
export type PublicProfile = {
  id: string;
  displayName?: string;
  handle?: string;
  avatarUrl?: string;
  bio?: string;
  email?: string;
  vendor?: boolean;
  youtubeChannelUrl?: string;
};

export function needsTermsAcceptance(profile: UserProfile | null | undefined): boolean {
  return !!profile && profile.termsAcceptedVersion !== CURRENT_TERMS_VERSION;
}

export function getPublicProfile(id: string) {
  return apiFetch<PublicProfile>(`/users/${id}`);
}

/** Map signed-in profile to the public profile shape for own-profile fallback. */
export function userProfileToPublic(profile: UserProfile): PublicProfile {
  return {
    id: profile.id,
    displayName: profile.displayName,
    handle: profile.handle,
    avatarUrl: profile.avatarUrl,
    bio: profile.bio,
    email: profile.email,
    vendor: profile.vendor,
    youtubeChannelUrl: profile.youtubeChannelUrl,
  };
}

export function patchProfile(
  body: Partial<
    Pick<UserProfile, 'displayName' | 'handle' | 'bio' | 'email' | 'youtubeChannelUrl' | 'profilePublic'>
  >,
) {
  return apiFetch<void>('/users/me', {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

export function acceptTerms() {
  return apiFetch<UserProfile>('/users/me/accept-terms', {
    method: 'POST',
    body: JSON.stringify({ version: CURRENT_TERMS_VERSION }),
  });
}

export function syncProfile() {
  return apiFetch<UserProfile>('/users/me/sync', { method: 'POST', body: '{}' });
}

export function deleteAccount() {
  return apiFetch<void>('/users/me', { method: 'DELETE' });
}
